import React, { useState, useEffect, useCallback } from "react";
import ExcelJS from 'exceljs';
import { Grid as GridLoading } from 'react-loader-spinner';
import Highlighter from "react-highlight-words";
import { styled } from '@mui/material/styles';
import { theme } from '../../theme';
import { OpenAIClient, AzureKeyCredential } from "@azure/openai";
import config from '../../config';

// Import MUI elements
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from "@mui/material/IconButton";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import SquareIcon from '@mui/icons-material/Square';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from "@mui/material/TextField";
import Typography from '@mui/material/Typography';

// Import framework to manage datepicker
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Import icons
import AssistantPhotoIcon from '@mui/icons-material/AssistantPhoto';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CallIcon from '@mui/icons-material/Call';
import CategoryIcon from '@mui/icons-material/Category';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from '@mui/icons-material/Send';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Divider } from "@mui/material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        fontSize: 13,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const ModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 900,
    height: 650,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    p: 4,
    overflow: 'scroll',
    display: 'block'
};

const TranscriptModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1250,
    height: 750,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    p: 4,
    overflow: 'scroll',
    display: 'block'
};

function ComplianceSearch({ parentToChild }: { parentToChild: any }) {
    const { startDate, endDate, mode } = parentToChild;

    const [insightsData, setInsightsData] = useState<any>(null);
    const [insightsModalOpen, setInsightsModalOpen] = useState<boolean>(false);
    const [isInsightsDataLoading, setIsInsightsDataLoading] = useState<boolean>(true);
    const [data, setData] = useState<any>();
    const [error, setError] = useState<any>(null);

    const [inputValue, setInputValue] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const [selectedTranscript, setSelectedTranscript] = useState<[string, any[], string]>(['', [], '']);

    const [transcriptIds, setTranscriptIds] = useState<any[]>([]);
    const [groupInsightsData, setGroupInsightsData] = useState<any>([]);
    const [isGroupInsightsLoading, setIsGroupInsightsLoading] = useState<boolean>(true);
    const [groupInsightsModalOpen, setGroupInsightsModalOpen] = useState<boolean>(false);
    const [callReasonData, setCallReasonData] = useState<any>(null);
    const [agentProblemsData, setAgentProblemsData] = useState<any>(null);
    const [agentAdherenceData, setAgentAdherenceData] = useState<any>(null);
    const [generalSummaryData, setGeneralSummaryData] = useState<any>(null);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [highlightsData, setHighlightsData] = useState<any>([]);

    // Variables for sending a report
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [subject, setSubject] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [comments, setComments] = useState<string>('');
    const [frequency, setFrequency] = useState<string>('Daily');
    const [recurrences, setRecurrences] = useState<string>('1');
    const [rangeStartDate, setRangeStartDate] = useState<dayjs.Dayjs>(dayjs());
    const [rangeEndDate, setRangeEndDate] = useState<dayjs.Dayjs>(dayjs());
    const [excelFile, setExcelFile] = useState<any | null>(null);
    const Today = dayjs();

    const connectAzureOpenAI = async (transcript: string) => {
        setIsInsightsDataLoading(true);
        const endpoint = config.azureOpenAI.chatEndpoint;
        const apiKey = config.azureOpenAI.chatKey;
        const deploymentId = config.azureOpenAI.chatDeployment;

        if (!endpoint || !apiKey) {
            throw new Error('Azure OpenAI chat credentials are not configured in environment variables.');
        }

        const client = new OpenAIClient(
            endpoint,
            new AzureKeyCredential(apiKey)
        );

        const messages = [
            { role: "system",
                content: `
                    You are a highly advanced AI working for a bank who is analyzing transcripts of calls between customers of the bank and call center agents also working for the bank. "
                    "You will be provided many of these transcripts. You need to use your brain and NLP techniques to parse these transcripts for very specific features of the conversations that they contain. "
                    "You should provide the following parsed features for each transcript in a JSON format. The possible values that you can return for each feature are listed after the name of the feature including further instructions where applicable. "
                    "These Features include:\n\n"
                    "**Product**: Credit Card, Debit Card, Auto Loan, Mortgage Loan, Savings Account, "
                    "Checking Account, Personal Loan, Investment Services"
                    "**Reason**: Account Setup or Information, Transaction Related, "
                    "Technical Assistance, and Account Management". Specifically for product Mortgage Loan, the reasons are "Shopping for rate", "Preapproval", "Rate lock", "Low appraisal", "Not qualified for requested amount", "Missing documents".
                    "**Subreason**: Product Features Inquiry, Eligibility and Requirements, Update Personal Information, Online Banking Troubleshooting, Transaction Limits Inquiry, Transaction Failure, Unauthorized Transaction,"
                    "Account Opening Assistance, Mobile App Issues, Account Closure or Upgrade"
                    "**Call Type**: Information and Guidance, Existing Customer, Services Issue Resolution"
                    "**Summary**: generate a summary of the call using 200 words or less, the summary should capture all the important points of the call and include how the call ended good or bad and whether or not the issue was resolved"
                    "**Call Sentiment**: Summarize the sentiment of the call in three words or less, varying from very frustrated to very satisfied. For example, if the call is not resolved and the customer asks to speak to the manager"
                    "or clearly express anger then summarize the sentiment of the call as "very frustrated". If the call goes well and the customer clearly expresses happiness then summarize the call as "very satisfied". If the customer does not express much anger or happiness then summarize the sentiment of the call as "neutral"\n\n"
                    "**Resolved**: If the call was resolved or not by the agent. Call should end with agent either solving the customer issue or have completed some task to be resolved. If the agent still has to complete a task or customer's issue was not solved, then the call was unresolved. If the call was resolved and not redirected to another agent then return "Yes" else return "No".\n\n"
                    "**Next Steps**: If the call was not resolved then provide the next steps that the agent should take to resolve the issue. If the call was resolved then return "No further action required".\n\n"
                    "That is all you must return, now find examples below of what is expected of you:\n\n"
                    "The following is a real transcript with an example of the expected result you will produce after parsing the transcript:\n\n"
                    "Agent: Good morning, thank you for calling ABC Bank, my name is Chris. How can I assist you with your mortgage loan today?

                    Customer: Hi Chris, I'm in the process of buying a home and I'm worried about the mortgage rates going up. I've seen them change a lot recently. Can I do something to secure a lower rate now?
                    
                    Agent: I understand your concern about fluctuating mortgage rates. ABC Bank does offer an option to lock in your rate for a period of time. We can provide a 60-day rate lock which would protect you from any increases during that time. There is a fee associated with this service. Would you like more details?
                    
                    Customer: Yes, please. What's the fee and how do I go about locking in the rate?
                    
                    Agent: The fee for our 60-day rate lock is $500, which is non-refundable. To proceed, we would need a signed purchase agreement for the property and your mortgage application must be fully completed. Have you already signed a purchase agreement?
                    
                    Customer: No, I'm still looking for the right house. Is it possible to lock in a rate without that?
                    
                    Agent: I'm afraid we do require a signed purchase agreement to initiate a rate lock. This policy is in place to ensure that we are securing a rate for a committed purchase. It also helps us manage the risk associated with rate locks.
                    
                    Customer: Okay, I didn't know that. What should I do next then?
                    
                    Agent: The best course of action would be to continue your house search and once you have a signed purchase agreement, complete your mortgage application. After these steps are taken, we can certainly help you with locking in your rate.
                    
                    Customer: Alright, I'll focus on finding a house first then. Thank you for the information, Chris.
                    
                    Agent: You're very welcome. Remember, if you have any questions about the mortgage process or need assistance once you've found a home, don't hesitate to contact us. We're here to support you.
                    
                    Customer: I appreciate that. Thanks again for your help.
                    
                    Agent: It's my pleasure. Thank you for calling ABC Bank. Have a wonderful day and best of luck with your home search! [End of call]\n\n"
                    "***that is the end of the transcript, expected result from you is the following, but in JSON format as mentioned:\n\n"
                    "Product: Mortgage Loan"
                    "Reason: Account Setup or Information"
                    "Subreason: Product Features Inquiry"
                    "Call_Type: Information and Guidance"
                    "Summary: customer contacts a bank's customer service to inquire about opening a savings account. The agent details the account's features, including competitive interest rates at 1.5% per annum (subject to market changes), no monthly maintenance fees, and the convenience of managing the account online. The customer expresses satisfaction with the online banking capabilities and clarifies there are no monthly fees, though some transactions may incur charges. Deciding to proceed with opening an account, the customer provides personal information required for setup. The agent assists with the account creation process, promising an email confirmation upon completion. The customer appreciates the assistance and excellent customer service, and the agent offers further help if needed in the future, ending the call on a positive note."
                    "Call_Sentiment:Satisfied"
                    "Resolved: Yes"
                    "Next_Steps: No further action required"***
                `
            },
            { 
                role: "user",
                content: transcript
            },
        ];

        // console.log(`Messages: ${messages.map((m) => m.content).join("\n")}`);

        const events = await client.streamChatCompletions(deploymentId, messages, { maxTokens: 800, temperature: 0.2, responseFormat: {"type": "json_object"}});
        // console.log(events);
        let chatbotResponse = "";

        for await (const event of events) {
            for (const choice of event.choices) {
                const delta = choice.delta?.content;
                if (delta !== undefined) {
                    chatbotResponse += delta;
                }
            }
        }

        // console.log(`${chatbotResponse}`);
        setInsightsData(JSON.parse(chatbotResponse));
        setIsInsightsDataLoading(false);
    };

    // console.log(insightsData);

    const getTranscriptIds = () => {
        const ids = data.map((row: any) => row['TranscriptID']);
        setTranscriptIds(ids);
    }

    // Get group insights data
    useEffect(() => {
        setIsGroupInsightsLoading(true);
        // console.log(transcriptIds);
        const fetchData = async () => {
            try {
                const response = await fetch(`/compliance_group_insights`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        transcript_ids: transcriptIds,
                    }),
                });
                
                const result = await response.json();
                // console.log(result);
                const resultArray = [Object.values(await result)];
                setGroupInsightsData(resultArray);

                const callReason = Object.entries(result).find(([key, value]) => key.toLowerCase() === 'common call reason')?.[1];
                const agentProblems = Object.entries(result).find(([key, value]) => key.toLowerCase() === 'common agent problems')?.[1];
                const agentAdherence = Object.entries(result).find(([key, value]) => key.toLowerCase() === 'common agent adherence')?.[1];
                const generalSummary = Object.entries(result).find(([key, value]) => key.toLowerCase() === 'general summary')?.[1];
                
                setCallReasonData(callReason);
                setAgentProblemsData(agentProblems);
                setAgentAdherenceData(agentAdherence);
                setGeneralSummaryData(generalSummary);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        if (transcriptIds.length > 0) {
            fetchData()
                .then(() => {
                    setIsGroupInsightsLoading(false);
            })
                .catch(error => console.error('Error in useEffect:', error));
        }
        // eslint-disable-next-line
    }, [transcriptIds]);

    // console.log(groupInsightsData);

    useEffect(() => {
        if (rangeStartDate && rangeEndDate && rangeStartDate.isAfter(rangeEndDate, 'day')) {
            setRangeStartDate(rangeEndDate);
        }
    }, [rangeStartDate, rangeEndDate]);

    // Handle excel file export
    const handleExport = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet 1');

        // Add headers
        const headers = Object.keys(data[0]);
        worksheet.addRow(headers);

        // Add data rows
        data.forEach((row: any) => {
            const rowData: any[] = []; // Explicitly define the type of rowData
            Object.values(row).forEach((cell) => {
                rowData.push(cell);
            });
            worksheet.addRow(rowData);
        });

        const excelBuffer = await workbook.xlsx.writeBuffer();

        setExcelFile(excelBuffer);
    };

    const handlePreview = () => {
        const blob = new Blob([excelFile], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = 'preview.xlsx';
        a.click();

        URL.revokeObjectURL(url);
    };

    const fetchData = useCallback(async (data: any) => {
        setIsLoading(true);
        try {
            const response = await fetch(`/search_compliance`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    keyword: data,
                }),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const result = await response.json();
            setData(result);
            setIsLoading(false);
        } catch (err) {
            setError(err || 'Something went wrong');
        }
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();
        fetchData(inputValue);
    }

    // const cleanTranscript = (transcript: string) => {
    //     let cleanTranscript = transcript.replace(/\*/g, '');

    //     // Find the index of the first occurrence of "Agent"
    //     let agentIndexOption1 = cleanTranscript.indexOf("Agent:");

    //     let agentIndexOption2 = cleanTranscript.indexOf("(Agent):");

    //     // If "Agent" is found, extract the substring starting from that index
    //     let finalTranscript
    //     if (agentIndexOption1 !== -1) {
    //         finalTranscript = cleanTranscript.substring(agentIndexOption1);
    //         return finalTranscript;
    //     } else if (agentIndexOption2 !== -1) {
    //         finalTranscript = cleanTranscript.substring(agentIndexOption2);
    //         return finalTranscript;
    //     } else {
    //         return cleanTranscript;
    //     }

    //     // return finalTranscript;
    // }

    const cleanTranscript = (transcript: string) => {
        let cleanTranscript = transcript.replace(/\*/g, '');

        // Find the index of the first occurrence of "Agent"
        let agentIndex = cleanTranscript.indexOf("Agent:");

        // If "Agent" is found, extract the substring starting from that index
        let finalTranscript
        if (agentIndex !== -1) {
            finalTranscript = cleanTranscript.substring(agentIndex);
            return finalTranscript;
        } else {
            return cleanTranscript;
        }
    }

    const handleOpen = (row: any) => {

        let cleanTranscript = row['Transcript'].replace(/\*/g, '');

        let agentIndex = cleanTranscript.indexOf("Agent:");

        let finalTranscript
        if (agentIndex !== -1) {
            finalTranscript = cleanTranscript.substring(agentIndex);
        } else {
            finalTranscript = cleanTranscript;
        }

        const transcriptArray = finalTranscript.split(/(?=Agent(\s[A-Za-z]+)?:|Customer:)/)
        .filter((segment: string) => segment !== undefined && segment.trim().split(/\s+/).length > 1) // Filter out undefined segments
        .map((segment: string, index: number) => {
            if (index % 2 === 0) {
                return segment.trim();
            } else {
                return `\n${segment}`;
            }
        });
    
        setSelectedTranscript([row['TranscriptID'], transcriptArray, row['Transcript']]);

        // Set highlights data
        const openingClosingArray = [];
        const prohibitedArray = [];
        const missedActionArray = [];
        const callerArray = [];
        const callThemeArray = [];
        const callRecordArray = [];

        (row['Scripted Opening Sentence'] !== 'NULL' && row['Scripted Opening Sentence'] !== null ? openingClosingArray.push(row['Scripted Opening Sentence']) : openingClosingArray.push(''));
        (row['Scripted Closing Sentence'] !== 'NULL' && row['Scripted Closing Sentence'] !== null ? openingClosingArray.push(row['Scripted Closing Sentence']) : openingClosingArray.push(''));
        (row['Prohibited Phrase Sentence'] !== 'NULL' && row['Prohibited Phrase Sentence'] !== null ? prohibitedArray.push(row['Prohibited Phrase Sentence']) : prohibitedArray.push(''));
        (row['Missed Actions Sentence'] !== 'NULL' && row['Missed Actions Sentence'] !== null ? missedActionArray.push(row['Missed Actions Sentence']) : missedActionArray.push(''));
        (row['Caller Authentication Sentence'] !== 'NULL' && row['Caller Authentication Sentence'] !== null ? callerArray.push(row['Caller Authentication Sentence']) : callerArray.push(''));
        (row['Call Theme Specific Disclosure Sentence'] !== 'NULL' && row['Call Theme Specific Disclosure Sentence'] !== null ? callThemeArray.push(row['Call Theme Specific Disclosure Sentence']) : callThemeArray.push(''));
        (row['Call Theme Disclosure Failure to Obtain Consent Sentence'] !== 'NULL' && row['Call Theme Disclosure Failure to Obtain Consent Sentence'] !== null ? callThemeArray.push(row['Call Theme Disclosure Failure to Obtain Consent Sentence']) : callThemeArray.push(''));
        (row['Call Recording Disclosure Sentence'] !== 'NULL' && row['Call Recording Disclosure Sentence'] !== null ? callRecordArray.push(row['Call Recording Disclosure Sentence']) : callRecordArray.push(''));
        (row['Call Recording Failure to Obtain Consent Sentence'] !== 'NULL' && row['Call Recording Failure to Obtain Consent Sentence'] !== null ? callRecordArray.push(row['Call Recording Failure to Obtain Consent Sentence']) : callRecordArray.push(''));

        setHighlightsData([openingClosingArray, prohibitedArray, missedActionArray, callerArray, callThemeArray, callRecordArray]);

        setOpen(true);
    };

    const Highlight = ({ children, highlightIndex }: { children: React.ReactNode, highlightIndex: number }) => {
        const colors = [
            '#ffe404', // Opening and Closing
            '#ff9a91', // Prohibited_Phrase_Sentence #c8b424
            '#b7ad6c', // Missed_Actions_Sentence
            '#907c1c', // Caller_Authentication_Sentence
            '#c4c4cd', // Call_Theme_Specific_Disclosure_Sentence
            mode === 'light' ? '#2e2e38' : 'white', // Call_Recording_Disclosure_Sentence
        ];
        const fontColors = [
            'black', // Opening and Closing
            'black', // Prohibited_Phrase_Sentence
            'white', // Missed_Actions_Sentence
            'white', // Caller_Authentication_Sentence
            'black', // Call_Theme_Specific_Disclosure_Sentence
            mode === 'light' ? 'white' : 'black', // Call_Recording_Disclosure_Sentence
        ];
        const color = colors[highlightIndex % colors.length];
        const fontColor = fontColors[highlightIndex % fontColors.length];
        return <span style={{ backgroundColor: color, fontFamily: theme.fonts.primary, color: fontColor }}>{children}</span>;
    };

    // console.log(highlightsData);

    const handleClose = () => {
        setOpen(false);
        setExcelFile(null);
    };

    const handleInsightsButtonClick = (transcript: string) => {
        connectAzureOpenAI(transcript);
        setOpen(false);
        setInsightsModalOpen(true);
    };

    // Send report button modal
    const handleSendReport = () => {
        //console.log('Sending report:', { email, comments });
        setIsModalOpen(false);
    };
    
    const openModal = () => {
        setIsModalOpen(true);
    };
    
    const closeModal = () => {
        setIsModalOpen(false);
    };

    // Insights buttons modal
    const handleCloseInsightsModal = () => setInsightsModalOpen(false);

    const handleCloseGroupInsightsModal = () => setGroupInsightsModalOpen(false);

    useEffect(() => {
        var init = '';
        fetchData(init);
    }, [fetchData, startDate, endDate]);

    error !== null && console.log(error);

    // console.log(data);
    
    return (
        <div className="ops-search-wrapper">
            <div className="exec-search">
                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                >
                    <TextField className="search-form" 
                    id="outlined-basic" 
                    label="Type your text here" 
                    variant="outlined" 
                    type="text" 
                    value={inputValue} 
                    onChange={handleInputChange} 
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleSubmit(e);
                        }
                    }}
                    />
                    <IconButton
                    size="small" 
                    onClick={(e) => {
                        handleSubmit(e);
                    }}>
                        <SearchIcon fontSize="inherit" />
                    </IconButton>
                </Box>
                <div className="ops-search-buttons">
                    <Button className="search-button" variant="contained" endIcon={<SendIcon />} onClick={() => {
                        handleExport();
                        openModal();
                    }}>
                        Send Report
                    </Button>
                    {data && data.length > 0 && (
                        <Button className="search-button" variant="contained" endIcon={<AutoAwesomeIcon />} 
                            onClick={() => {
                                getTranscriptIds();
                                setGroupInsightsModalOpen(true);
                            }}
                            >
                            AI Group Insights
                        </Button>
                    )}
                </div>
            </div>
            {data && !isLoading && data.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 550 }}>
                    <Table sx={{ minWidth: 500 }} stickyHeader aria-label="sticky table" id="myTable">
                        <TableHead>
                            <TableRow>
                                {/* <StyledTableCell className="table-header">Date</StyledTableCell> */}
                                <StyledTableCell className="table-header" align="center">Transcript ID</StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Call Type{mode === 'dark' ? <span className='shine'><AutoAwesomeIcon/></span> : <span className='ai-driven'><AutoAwesomeIcon/></span> }</StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Call Reason</StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Duration</StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Transcript <span style={{fontSize: 10}}>(Click to view)</span></StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Sentiment{mode === 'dark' ? <span className='shine'><AutoAwesomeIcon/></span> : <span className='ai-driven'><AutoAwesomeIcon/></span> }<br/><span style={{fontSize: 10}}>1 - 10</span></StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Additional Insights</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row: any) => (
                                <StyledTableRow key={row['TranscriptID']}>
                                    <StyledTableCell style={{ width: '7%' }} align="center">{row['TranscriptID']}</StyledTableCell>
                                    <StyledTableCell style={{ width: '15%' }} align="center">{row['Call Type']}</StyledTableCell>
                                    <StyledTableCell style={{ width: '15%' }} align="center">{row['Reason']}</StyledTableCell>
                                    <StyledTableCell style={{ width: '5%' }} align="center">{row['Duration']} min</StyledTableCell>
                                    <StyledTableCell style={{ width: '30%' }} align="center">
                                        <div className="table-transcript-link" onClick={() => handleOpen(row)}>
                                            {cleanTranscript(row['Transcript'].substr(0, 200)) + '...'}
                                        </div>
                                    </StyledTableCell>
                                    <StyledTableCell style={{ width: '10%' }} align="center">{row['Call Sentiment']}</StyledTableCell>
                                    <StyledTableCell style={{ width: '13%' }} align="center">
                                        <Button className="modal-btn" variant="contained" endIcon={<AutoAwesomeIcon />} onClick={() => {handleInsightsButtonClick(row['Transcript'])}}>
                                            AI Insights
                                        </Button>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : isLoading ? (
                <div className="search-loading-area">
                    <Skeleton width="100%" height="200px" />
                    <span className="centered-text">
                        <GridLoading
                        visible={true}
                        height="15"
                        width="15"
                        color={mode === 'dark' ? 'white' : 'black'}
                        ariaLabel="grid-loading"
                        radius="12.5"
                        wrapperStyle={{}}
                        wrapperClass="grid-wrapper"
                        />
                        Retrieving records...
                    </span>
                </div>
            ) : data && data.length === 0 && (
                <div className="no-records-found">
                    <span>No records found</span>
                </div>
            )}
            
            <Modal
                open={groupInsightsModalOpen}
                onClose={handleCloseGroupInsightsModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                className="modal-global"
            >
                <Box className='modal-style' sx={ModalStyle}>
                    <div className="modal-header">
                        <h3><AutoAwesomeIcon /> AI Group Insights</h3>
                        <Button className="modal-btn" variant="contained" onClick={handleCloseGroupInsightsModal}>
                            Close
                        </Button>
                    </div>
                    <div id="modal-modal-description">
                        {!isGroupInsightsLoading && groupInsightsData ? (
                            <Box sx={{ flexGrow: 1 }}>
                                <Grid container spacing={2} className="modal-summary-notes">
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card disclosure-gi-card group-insights">
                                            <CardContent>
                                                <h4><CallIcon/>Common Call Reason</h4>
                                                {callReasonData ? (
                                                    <span>{callReasonData}</span>
                                                ) : (
                                                    <span>No data available</span>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card disclosure-gi-card group-insights">
                                            <CardContent>
                                                <h4><SupportAgentIcon/>Common Agent Adherence</h4>
                                                {agentAdherenceData ? (
                                                    <span>{agentAdherenceData}</span>
                                                ) : (
                                                    <span>No data available</span>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card disclosure-gi-card group-insights">
                                            <CardContent>
                                                <h4><ReportProblemIcon/>Common Agent Problems</h4>
                                                {agentProblemsData ? (
                                                    <span>{agentProblemsData}</span>
                                                ) : (
                                                    <span>No data available</span>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card disclosure-gi-card group-insights">
                                            <CardContent>
                                                <h4><SummarizeIcon/>General Summary</h4>
                                                {generalSummaryData ? (
                                                    <span>{generalSummaryData}</span>
                                                ) : (
                                                    <span>No data available</span>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <div className='position-reletive'>
                                <Skeleton width="100%" height="450px" />
                                <span className="centered-text">
                                    <GridLoading
                                    visible={true}
                                    height="15"
                                    width="15"
                                    color={mode === 'dark' ? 'white' : 'black'}
                                    ariaLabel="grid-loading"
                                    radius="12.5"
                                    wrapperStyle={{}}
                                    wrapperClass="grid-wrapper"
                                    />
                                    AI is generating group insights...
                                </span>
                            </div>
                        )}
                    </div>
                </Box>
            </Modal>

            {selectedTranscript[1].length > 0 && (
                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                    className="modal-global"
                >
                    <Box className='modal-style' sx={TranscriptModalStyle}>
                        <div className="modal-header">
                            <h3><SummarizeIcon/> Full Transcript</h3>
                            <Button className="modal-btn" variant="contained" endIcon={<AutoAwesomeIcon />} onClick={() => {
                                handleInsightsButtonClick(selectedTranscript[2]);
                            }}>
                                AI Insights
                            </Button>
                        </div>
                        <Box sx={{ flexGrow: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12} lg={7.5} xl={7.5}>
                                    <Card variant="outlined" className="get-insights-card">
                                        <CardContent>
                                            <Typography id="modal-modal-description transcript-show-modal" sx={{ mt: 2 }}>                                            
                                            {selectedTranscript[1].map((segment, index) => (
                                            <React.Fragment key={index}>
                                                <Highlighter
                                                highlightClassName='highlighted-sentence'
                                                searchWords={highlightsData.flat()}
                                                autoEscape={true}
                                                caseSensitive={true}
                                                textToHighlight={segment}
                                                highlightTag={(props: { children: React.ReactNode }) => (
                                                    <Highlight
                                                        {...props}
                                                        highlightIndex={highlightsData.findIndex((highlight: string) =>
                                                            highlight.includes(props.children as string)
                                                        )}
                                                    />
                                                )}
                                                />
                                                <br />
                                                <br />
                                            </React.Fragment>
                                            ))}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} lg={4.5} xl={4.5}>
                                    <Card variant="outlined" className="transcript-card transcript-highlighting highlight-legend">
                                        {highlightsData && highlightsData.length > 0 ? (
                                        <CardContent>
                                            <div className='legend-flex'>
                                                <div>
                                                    <p><SquareIcon className='color-prohibit'/> Prohibited Phrases</p>
                                                    <span>{(highlightsData[1][0] === 'NULL' || highlightsData[1][0] === undefined || highlightsData[1][0] === '') ? <span className='green-arrow'><CancelIcon/> Not detected</span> : <span className='red-arrow'><CheckCircleIcon/> Detected</span>}</span>
                                                </div>
                                                <div>
                                                    <p><SquareIcon className='color-action' /> Missed Actioning</p>
                                                    <span>{(highlightsData[2][0] === 'NULL' || highlightsData[2][0] === undefined || highlightsData[2][0] === '') ? <span className='green-arrow'><CancelIcon/> Not detected</span> : <span className='red-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                                <div>
                                                    <p><SquareIcon className='color-caller'/> Caller Authentication</p>
                                                    <span>{(highlightsData[3][0] === 'NULL' || highlightsData[3][0] === undefined || highlightsData[3][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                            </div>
                                            <Divider/>

                                            <div className='legend-flex'>
                                                <div>
                                                    <p><SquareIcon className='color-oc'/> Scripted Opening</p>
                                                    <span>{(highlightsData[0][0] === 'NULL' || highlightsData[0][0] === undefined || highlightsData[0][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span>}</span>
                                                </div>
                                                <div>
                                                    <p><SquareIcon className='color-oc'/> Scripted Closing</p>
                                                    <span>{(highlightsData[0][1] === 'NULL' || highlightsData[0][1] === undefined || highlightsData[0][1] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span>}</span>
                                                </div>
                                                
                                                <div>
                                                    <p><SquareIcon className='color-theme'/> Specific Call Theme Disclosure</p>
                                                    <span>{(highlightsData[4][0] === 'NULL' || highlightsData[4][0] === undefined || highlightsData[4][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                            </div>
                                            <Divider/>

                                            <div className='legend-flex'>
                                                <div>
                                                    <p><SquareIcon className='color-record'/> Call Recording Disclosure</p>
                                                    <span>{(highlightsData[5][0] === 'NULL' || highlightsData[5][0] === undefined || highlightsData[5][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                                <div>
                                                    <p><SquareIcon className='color-record'/> Consent Failure: Call Recording</p>
                                                    <span>{(highlightsData[5][1] === 'NULL' || highlightsData[5][1] === undefined || highlightsData[5][1] === '') ? <span className='green-arrow'><CancelIcon/> Not detected</span> : <span className='red-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                                <div>
                                                    <p><SquareIcon className='color-theme'/> Consent Failure: Theme Disclosure</p>
                                                    <span>{(highlightsData[4][1] === 'NULL' || highlightsData[4][1] === undefined || highlightsData[4][1] === '') ? <span className='green-arrow'><CancelIcon/> Not detected</span> : <span className='red-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                            </div>                                        
                                            
                                        </CardContent>
                                        ) : (
                                        <Skeleton width="100%" height="100%" />
                                        )}
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Modal>
            )}
            <Modal
                className="modal-global"
                open={insightsModalOpen}
                onClose={handleCloseInsightsModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box className='modal-style' sx={ModalStyle}>
                    <div className="modal-header">
                        <h3><AutoAwesomeIcon /> AI Insights</h3>
                        <Button className="modal-btn" variant="contained" onClick={handleCloseInsightsModal}>
                            Close
                        </Button>
                    </div>
                    <div id="modal-modal-description">
                        {!isInsightsDataLoading && insightsData ? (
                            <Box sx={{ flexGrow: 1 }}>
                                <Grid container spacing={2} className="modal-summary-notes">
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><SentimentNeutralIcon/>Call Sentiment</h4>
                                                <span>{(insightsData && insightsData.Call_Sentiment !== '') ? insightsData.Call_Sentiment : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><CallIcon/>Call Type</h4>
                                                <span>{(insightsData && insightsData.Call_Type !== '') ? insightsData.Call_Type : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><CategoryIcon/>Product</h4>
                                                <span>{(insightsData && insightsData.Product !== '') ? insightsData.Product : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4>
                                                    {insightsData && (insightsData.Resolved === 'YES' || insightsData.Resolved === 'Yes') ? (<CheckCircleIcon/>) : (<CancelIcon/>)}
                                                    Resolved
                                                </h4>
                                                <span>{(insightsData && insightsData.Resolved !== '') ? insightsData.Resolved : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><ContactSupportIcon/>Call Reason</h4>
                                                <span>{(insightsData && insightsData.Reason !== '') ? insightsData.Reason : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><ContactSupportIcon/>Sub Reason</h4>
                                                <span>{(insightsData && insightsData.Subreason !== '') ? insightsData.Subreason : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><SummarizeIcon/>Summary</h4>
                                                <span>{(insightsData && insightsData.Summary !== '') ? insightsData.Summary : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><AssistantPhotoIcon/>Next Steps</h4>
                                                <span>{(insightsData && insightsData.Next_Steps !== '') ? insightsData.Next_Steps : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <div className='position-reletive'>
                                <Skeleton width="100%" height="450px" />
                                <span className="centered-text">
                                    <GridLoading
                                    visible={true}
                                    height="15"
                                    width="15"
                                    color={mode === 'dark' ? 'white' : 'black'}
                                    ariaLabel="grid-loading"
                                    radius="12.5"
                                    wrapperStyle={{}}
                                    wrapperClass="grid-wrapper"
                                    />
                                    AI is generating insights...
                                </span>
                            </div>
                        )}
                    </div>
                </Box>
            </Modal>
           
            <Modal className='exec-send-modal modal-global' open={isModalOpen} onClose={closeModal} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box className='modal-style' sx={ModalStyle}>
                    <h3><SendIcon/> Report Form</h3>
                    <div>
                        <TextField
                        label="Subject"
                        variant="outlined"
                        fullWidth
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        margin="normal"
                        />
                        <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        margin="normal"
                        />
                        <TextField
                        label="Additional Comments"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        margin="normal"
                        />
                        <FormControl fullWidth className='send-report-dropdown'>
                            <InputLabel id="select-label">Report Frequency</InputLabel>
                            <Select
                                labelId="select-label"
                                id="select"
                                value={frequency}
                                onChange={(e) => {setFrequency(e.target.value);}}
                            >
                                <MenuItem value="Daily">Daily</MenuItem>
                                <MenuItem value="Weekly">Weekly</MenuItem>
                                <MenuItem value="Monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Num of Recurrences"
                            variant="outlined"
                            fullWidth
                            type="number"
                            value={recurrences}
                            onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (!isNaN(value) && value >= 0 && value <= 365) {
                                    setRecurrences(value.toString());
                                } else if (e.target.value === '') {
                                    setRecurrences('');
                                }
                            }}
                            margin="normal"
                            inputProps={{ inputMode: 'numeric', max: 365 }}
                        />
                        <p className='text-dark-mode'>Recurrence Range:</p>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
                            <div className='send-report-datepickers'>
                                <DatePicker
                                    className='exec-date-picker'
                                    label="Start"
                                    value={rangeStartDate}
                                    onChange={(date) => {
                                        if (date && date.isBefore(Today, 'day')) {
                                            setRangeStartDate(Today);
                                        } else {
                                            setRangeStartDate(date || Today);
                                        }
                                    }}
                                />
                                <span className='text-dark-mode'> to </span>
                                <DatePicker
                                    className='exec-date-picker'
                                    label="End"
                                    value={rangeEndDate}
                                    onChange={(date) => {
                                        if (date && date.isBefore(Today, 'day')) {
                                            setRangeEndDate(Today);
                                        } else if (rangeStartDate && date && date.isBefore(rangeStartDate, 'day')) {
                                            setRangeEndDate(rangeStartDate);
                                        } else {
                                            setRangeEndDate(date || Today);
                                        }
                                    }}
                                />
                            </div>
                        </LocalizationProvider>
                        {excelFile && (
                            <div className="table-file-export">
                                <p onClick={handlePreview}>Preview Excel File</p>
                                {/* <input type="hidden" name="data" value={excelFile} /> */}
                            </div>
                        )}
                    </div>
                    <div>
                        <Button className='exec-send-btn' variant="contained" onClick={handleSendReport}>Send</Button>
                    </div>
                </Box>
            </Modal>
        </div>
    );
}

export default ComplianceSearch;
